// src/components/VendorsDisplay.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaBuilding, FaGlobe, FaSearch } from 'react-icons/fa';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');

const TIER_COLORS = {
  Bronze: '#cd7f32',
  Silver: '#9e9e9e',
  Gold: '#ffd700',
  Platinum: '#6B8E23',
  Diamond: '#0d6efd'
};

const VendorsDisplay = ({ showHeader = true, maxItems = null, filterCategory = null }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(filterCategory || '');
  const [selectedCountry, setSelectedCountry] = useState('');

  const CATEGORIES = [
    'Medical Equipment & Devices', 'Respiratory Care Equipment', 'Ventilators & CPAP Machines',
    'Personal Protective Equipment (PPE)', 'Diagnostic Equipment', 'Oxygen Therapy Equipment',
    'Nebulizers & Inhalers', 'Patient Monitoring Systems', 'Software & Technology Solutions',
    'Training & Education Services', 'Laboratory Equipment', 'Hospital Furniture & Supplies',
    'Pharmaceutical Products', 'Rehabilitation Equipment', 'Telehealth Solutions', 'Other'
  ];

  useEffect(() => {
    fetchVendors();
  }, [search, selectedCategory, selectedCountry]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCountry) params.append('country', selectedCountry);

      const res = await fetch(`${API_BASE}/api/vendors/public?${params}`);
      const data = await res.json();
      if (data.success) {
        setVendors(maxItems ? data.data.slice(0, maxItems) : data.data);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    return `${API_BASE}${logo}`;
  };

  // Get unique countries from vendors
  const countries = [...new Set(vendors.map(v => v.country).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted mt-3">Loading vendor directory...</p>
      </div>
    );
  }

  return (
    <section className="py-4 py-lg-5" style={{ background: 'linear-gradient(135deg, #f0f4f0 0%, #6B8E23 100%)' }}>
      <div className="container py-4 py-lg-5">
        {showHeader && (
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-3" style={{ color: '#6B8E23' }}>Our Industry Partners</h2>
            <p className="text-muted lead">Trusted companies supporting respiratory care across Africa</p>
            <div className="mx-auto" style={{ width: '80px', height: '4px', background: 'linear-gradient(90deg, #6B8E23 0%, #556B2F 100%)', borderRadius: '2px' }}></div>
          </div>
        )}

        {/* Filters */}
        {showHeader && (
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-white"><FaSearch style={{ color: '#6B8E23' }} /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search vendors..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-4">
              <select className="form-select" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
                <option value="">All Categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <select className="form-select" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}>
                <option value="">All Countries</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}

        {vendors.length === 0 ? (
          <div className="text-center py-5">
            <FaBuilding size={48} className="text-muted mb-3" />
            <p className="text-muted fs-5">No approved vendors found{search ? ' matching your search' : ''}.</p>
            <Link to="/get-involved/vendor-membership" className="btn text-white mt-2" style={{ backgroundColor: '#6B8E23' }}>
              Become a Vendor Partner
            </Link>
          </div>
        ) : (
          <>
            <div className="row g-3 g-lg-4">
              {vendors.map(vendor => {
                const logoUrl = getLogoUrl(vendor.logo);
                const tierColor = TIER_COLORS[vendor.vendor_tier] || '#6B8E23';
                return (
                  <div key={vendor.id} className="col-12 col-sm-6 col-lg-4">
                    <div
                      className="card h-100 border-0 shadow-sm"
                      style={{ transition: 'all 0.3s ease', borderTop: `4px solid ${tierColor}` }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
                    >
                      <div className="card-body p-4">
                        {/* Logo */}
                        <div className="mb-3 d-flex align-items-center justify-content-center" style={{ height: '80px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={vendor.company_name}
                              style={{ maxHeight: '70px', maxWidth: '100%', objectFit: 'contain' }}
                              onError={e => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div style={{ display: logoUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <FaBuilding size={40} style={{ color: tierColor, opacity: 0.5 }} />
                          </div>
                        </div>

                        {/* Tier badge */}
                        <div className="d-flex align-items-start justify-content-between mb-2">
                          <h5 className="fw-bold mb-0" style={{ fontSize: '1rem', color: '#1a1a1a' }}>{vendor.company_name}</h5>
                          <span className="badge ms-2 flex-shrink-0" style={{ backgroundColor: tierColor, fontSize: '0.7rem' }}>
                            {vendor.vendor_tier}
                          </span>
                        </div>

                        <p className="text-muted small mb-2">
                          <i className="bi bi-geo-alt me-1"></i>{vendor.country}
                        </p>

                        {vendor.category && (
                          <p className="small mb-2">
                            <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>{vendor.category}</span>
                          </p>
                        )}

                        {vendor.description && (
                          <p className="text-muted small mb-3" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {vendor.description}
                          </p>
                        )}

                        {vendor.website && (
                          <a
                            href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center w-100 justify-content-center"
                          >
                            <FaGlobe className="me-2" /> Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {showHeader && (
              <div className="text-center mt-5">
                <p className="text-muted mb-3">Are you a company or supplier serving the respiratory care sector?</p>
                <Link to="/get-involved/vendor-membership" className="btn btn-lg text-white" style={{ backgroundColor: '#6B8E23' }}>
                  Become an Industry Partner
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default VendorsDisplay;
